import { getId } from 'app-components/flux/core/resourceStoreTypes';
import { getCurrentDateString } from 'app-components/flux/date/dateSelectors';
import { transactionsSelectors, transactionsTypes } from 'app-components/flux/transactions';
import type { AllocationProps, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { createAppSelector, createSelectorShallowEqual } from 'app-components/utils/core/reselect';
import { isList } from 'immutable';

export type InvoiceBucket = 'unpaid' | 'drafts' | 'recurring' | 'paid';

export const getInvoices = createSelectorShallowEqual(transactionsSelectors.getResourcesById, (transactions) =>
  transactions
    .valueSeq()
    .filter((txn) => txn.type === 'INVOICE' && txn.subtype === 'INVOICE')
    .toArray(),
);

export const selectInvoicePayments = createSelectorShallowEqual(
  [transactionsSelectors.getResourcesById],
  (transactions) =>
    transactions
      .valueSeq()
      .filter((txn) => txn.type === 'CASH_FLOW' && txn.subtype === transactionsTypes.TransactionSubType.InvoicePayment)
      .toArray(),
);

export const selectAllocations = createSelectorShallowEqual([selectInvoicePayments], (payments) =>
  payments.flatMap((txn) => (isList(txn.allocations) ? txn.allocations.toArray() : txn.allocations ?? [])),
);

export const selectInvoicesLastPaidDate = createSelectorShallowEqual([selectInvoicePayments], (payments) => {
  const lastPaidDates: Record<string, string | undefined> = {}; // Will be undefined for invoices that do not have a payment
  for (const payment of payments) {
    if (payment.allocations) {
      // forEach because it could be a List
      payment.allocations.forEach((alloc) => {
        if (
          alloc.txnId &&
          payment.postedOn &&
          (!lastPaidDates[alloc.txnId] || (lastPaidDates[alloc.txnId] ?? '') < payment.postedOn)
        ) {
          lastPaidDates[alloc.txnId] = payment.postedOn;
        }
      });
    }
  }
  return lastPaidDates;
});

// We return an array because the same invoice can show up in multiple buckets
export const calculateInvoiceBucket = (invoice: TransactionProps, amountPaid: number): InvoiceBucket[] => {
  if (!invoice.sentOn) {
    return ['drafts'];
  }
  if (amountPaid === 0) {
    return ['unpaid'];
  }
  const outstandingBalance = invoice.amount - amountPaid;
  if (outstandingBalance > 0) {
    return ['unpaid', 'paid'];
  }
  return ['paid'];
  // TODO: recurring
};

export const calculatePaidAmountForInvoice = (id: string, allocations: ReadonlyArray<AllocationProps>): number =>
  allocations.filter((alloc) => alloc.txnId === id).reduce((sum, alloc) => (alloc.amount ?? 0) + sum, 0);

export const selectPaidAmountForInvoices = createSelectorShallowEqual(
  [getInvoices, selectAllocations],
  (invoices, allocations) =>
    Object.fromEntries(
      invoices.map((invoice) => [getId(invoice), calculatePaidAmountForInvoice(getId(invoice), allocations)]),
    ),
);

export const getInvoicesBuckets = createAppSelector(
  [getInvoices, selectPaidAmountForInvoices],
  (invoices, paidAmounts): Record<string, InvoiceBucket[]> =>
    Object.fromEntries(
      invoices.map((invoice) => [getId(invoice), calculateInvoiceBucket(invoice, paidAmounts[getId(invoice)])]),
    ),
);

export const getInvoiceIds = createSelectorShallowEqual(
  [getInvoices, getInvoicesBuckets, (_: unknown, filter: InvoiceBucket) => filter],
  (invoices, statuses, filter) => invoices.map(getId).filter((id) => statuses[id].includes(filter)),
);

export type InvoiceStatus = 'overdue' | 'partial' | 'sent' | 'paid' | 'draft';
export const calculateInvoiceStatus = (invoice: TransactionProps, amountPaid: number, today: string): InvoiceStatus => {
  if (invoice.amount <= amountPaid) {
    return 'paid';
  }
  if (invoice.dueOn && invoice.dueOn < today) {
    return 'overdue';
  }
  if (amountPaid > 0) {
    return 'partial';
  }
  if (invoice.sentOn) {
    return 'sent';
  }
  return 'draft';
};
export const selectInvoiceChipStatuses = createAppSelector(
  [getInvoices, selectPaidAmountForInvoices, getCurrentDateString],
  (invoices, paidAmounts, today) =>
    Object.fromEntries(
      invoices.map((invoice) => [getId(invoice), calculateInvoiceStatus(invoice, paidAmounts[getId(invoice)], today)]),
    ),
);

export const getInvoiceStatusByInvoiceId = createAppSelector(
  [selectInvoiceChipStatuses, (_state: unknown, id: string) => id],
  (statuses, id) => statuses[id],
);

export const selectPaidAmountForProjectId = createAppSelector(
  [
    getInvoices,
    selectPaidAmountForInvoices,
    (_: unknown, props: { projectId: string; customerId: string }) => props.projectId,
    (_: unknown, props: { projectId: string; customerId: string }) => props.customerId,
  ],
  (invoices, paidAmounts, projectId, customerId) => {
    return invoices
      .filter(
        (invoice) =>
          invoice.projectId === projectId ||
          (!invoice.projectId && projectId === 'no-project' && invoice.customerId === customerId),
      )
      .reduce((sum, invoice) => sum + paidAmounts[getId(invoice)], 0);
  },
);

export const selectPaymentsForInvoiceId = createSelectorShallowEqual(
  [(state, id: string) => transactionsSelectors.getTransactionById(state, id)?.allocations, selectInvoicePayments],
  (allocations, allPayments) => {
    if (!allocations) {
      return [];
    }

    const paymentsForId = allPayments.filter((paymentTxn) =>
      allocations.find((alloc) => alloc.txnId === paymentTxn.id),
    );
    paymentsForId.sort((a, b) => (a.postedOn < b.postedOn ? 1 : -1));
    return paymentsForId;
  },
);
